import {
  CMS_CONFIG,
  getCurrentUserType,
  ICoverageToolCongifg,
  IUserType,
  IProduct,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import {
  ITrackError,
  ITrackErrorRes,
  LoggerInstance,
  TEventName,
  AppMobile,
} from '@vfit/shared/data-access';
import { API, PAGES } from '@vfit/shared/data-access';
import {
  IAddressResponse,
  ICmsConfigAddressManual,
  IEditAddressCmsConfig,
} from '@vfit/shared/models';
import { COVERAGE_TOOL_FLOW } from './coverageToolModal.flow';
import { IStepCard } from '../StepperStateCards/stepperStateCards.models';
import { useAddressGeocoder } from '@vfit/shared/hooks';
import * as yup from 'yup';
import {
  IAddressObject,
  ICoverageToolType,
  IGeoAddressElab,
  IInputs,
} from './coverageToolModal.models';

export const OVERRIDE_DEBUG_MODE =
  process.env?.['NEXT_PUBLIC_ENABLE_COVERAGE_TOOL_DEBUG'] === 'TRUE' || false;
export const ENABLE_DEBUG = OVERRIDE_DEBUG_MODE || process.env['NODE_ENV'] === 'development';

const getCoverageToolFlow = (isLockIn = false) => {
  const isApp = AppMobile.checkIsApp();
  const isLoggedUser = getCurrentUserType() === IUserType.LOGGED_USER;
  if (isLockIn && !isApp && !isLoggedUser) {
    return COVERAGE_TOOL_FLOW.LOCKIN;
  }
  return COVERAGE_TOOL_FLOW.DEFAULT;
};

const coverageToolLockInMessage = () => {
  const dataFromCms = useCmsConfig(
    CMS_CONFIG[PAGES.CONSUMER],
    API.CMS_GET_COVERAGE_TOOL_CONFIG
  ) as ICoverageToolCongifg;
  const errorNoOffer = {
    title: dataFromCms?.coveragetool?.lockIn?.modalErrorNoOffer?.title || '',
    description: dataFromCms?.coveragetool?.lockIn?.modalErrorNoOffer?.description || '',
    editLabel: dataFromCms?.coveragetool?.lockIn?.modalErrorNoOffer?.editLabel || '',
    submitLabel: dataFromCms?.coveragetool?.lockIn?.modalErrorNoOffer?.customAction?.title || '',
  };

  const errorNoCoverage = {
    title: dataFromCms?.coveragetool?.lockIn?.modalErrorNoCoverage?.title || '',
    description: dataFromCms?.coveragetool?.lockIn?.modalErrorNoCoverage?.description || '',
    editLabel: dataFromCms?.coveragetool?.lockIn?.modalErrorNoCoverage?.editLabel || '',
    submitLabel: dataFromCms?.coveragetool?.lockIn?.modalErrorNoCoverage?.clickToCall?.title || '',
  };

  return { errorNoOffer, errorNoCoverage };
};

const organizeFlowsAppend = (
  flowCards: IStepCard[],
  enableAppend: boolean,
  idFromStep: string,
  appendSteps: IStepCard[]
) => {
  const indexAppend = flowCards.findIndex((el) => el.title === idFromStep);
  let organizedFlows = flowCards.slice(0, indexAppend + 1);
  const lastFlows = flowCards.slice(indexAppend + 1);
  if (enableAppend) {
    const alreadyPresent = appendSteps[0].title === lastFlows[0].title;
    if (alreadyPresent) return flowCards;
    organizedFlows = organizedFlows.concat(appendSteps);
    organizedFlows = organizedFlows.concat(lastFlows);
  } else if (lastFlows[0].title === appendSteps[0].title) {
    const removedAppendInfo = lastFlows.slice(appendSteps.length);
    organizedFlows = organizedFlows.concat(removedAppendInfo);
  } else if (appendSteps?.findIndex((aS) => aS.title === lastFlows[0].title) > -1) {
    const removedAppendInfo = lastFlows.slice(1);
    organizedFlows = organizedFlows.concat(removedAppendInfo);
  } else {
    organizedFlows = organizedFlows.concat(lastFlows);
  }
  return organizedFlows;
};

const getAddress = () => {
  const createdAddressSaved = localStorage.getItem('createdAddress');
  const createdAddress: IAddressResponse =
    !!createdAddressSaved && JSON.parse(createdAddressSaved as string);
  LoggerInstance.debug('createdAddress : ', createdAddress);
  if (!createdAddress) return undefined;
  const dataToDisplay = [
    `${(createdAddress as IAddressResponse)?.street || ''} ${
      (createdAddress as IAddressResponse)?.streetNumber || ''
    }`,
    `${(createdAddress as IAddressResponse)?.postalCode || ''} ${
      (createdAddress as IAddressResponse)?.city
    }`,
    `${(createdAddress as IAddressResponse)?.stateOrProvince || ''}`,
  ];
  return {
    ...createdAddress,
    displayedAddress: dataToDisplay.join(', '),
  };
};

const getTrackError = (error: ITrackError, step?: string): ITrackErrorRes => {
  const pageError = `
  ${error?.error?.url || ''}_
  ${error?.error?.status || ''}_
  ${error?.errorType || ''}_
  ${error?.isBlocking ? 'blk' : 'nblk'}_
  ${step || ''}`;
  return {
    event_name: 'page_error' as TEventName,
    event_label_track: 'page_error',
    event_category: 'error',
    page_error: pageError,
    page_error_code: error?.errorCode || '',
    page_type: 'error page',
  };
};

const LOCKIN_ERROR_CODES = {
  EMPTY_CAMPAIGNS_LIST: 'EMPTY_CAMPAIGNS_LIST',
  NO_USEFUL_CAMPAIGNS_FOUND: 'NO_USEFUL_CAMPAIGNS_FOUND',
  CAMPAIGNS_GENERIC_ERROR: 'CAMPAIGNS_GENERIC_ERROR',
};

const GetAddressObjectFromString = async (text: string) =>
  Promise.resolve(await useAddressGeocoder(text));

const retrieveAddress = (add: IAddressObject) => {
  let addressString = '';
  Object.values(add).map((el: string) => {
    if (el) addressString = addressString.concat(el, ', ');
    return null;
  });
  return addressString;
};

const checkCoverageToolType = (mutateLocation: IInputs): ICoverageToolType => {
  let typeCoverageTool = ICoverageToolType.AUTO;
  LoggerInstance.debug('Mutate location', mutateLocation);
  delete mutateLocation.completeAddress;
  Object.keys(mutateLocation).forEach((key) => {
    if (!mutateLocation[key]) typeCoverageTool = ICoverageToolType.MANUAL;
  });
  return typeCoverageTool;
};

const checkAddressFromValue = async (text: string) =>
  Promise.resolve(await GetAddressObjectFromString(text));

const getCoverageInputValidation = (coverageToolConfig: ICoverageToolCongifg) =>
  yup
    .object({
      placeId: yup.string().required(''), //  This is handled in AddressInput
      city: yup
        .string()
        .min(1, coverageToolConfig?.coveragetool?.errorMessage?.error0 || ' ')
        .matches(
          /^[a-zA-Z+"àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝ'"+\s]*$/g,
          coverageToolConfig?.coveragetool?.errorMessage?.error1 || ' '
        ),
      //  .required(coverageToolConfig.coveragetool.errorMessage.error0),
      street: yup
        .string()
        .min(1, coverageToolConfig?.coveragetool?.errorMessage?.error0 || ' ')
        .matches(
          /^[a-zA-Z+"-àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝ"+\s]*$/g,
          coverageToolConfig?.coveragetool?.errorMessage?.error1 || ' '
        ),
      // .required(coverageToolConfig.coveragetool.errorMessage.error0),
      streetNumber: yup
        .string()
        .test(
          'rangeTest',
          coverageToolConfig?.coveragetool?.errorMessage?.error1 || ' ',
          (value) => {
            if (value?.match(/^[1-9][0-9]{0,4}(?:\/[0-9]{1,4})?[a-zA-Z]?$/)) {
              return true;
            }
            return false;
          }
        )
        .required(coverageToolConfig?.coveragetool?.errorMessage?.error0),
    })
    .required();

const composeAddress = (value: any) => {
  const { address_components } = value;
  const address: IGeoAddressElab = {
    street: '',
    streetNumber: '',
    city: '',
    province: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    region: '',
  };

  address_components.forEach((v: any) => {
    if (v.types.includes('route')) address.street = v.long_name;
    else if (v.types.includes('administrative_area_level_3')) address.city = v.long_name;
    else if (v.types.includes('administrative_area_level_2')) address.province = v.short_name;
    else if (v.types.includes('administrative_area_level_1')) address.region = v.short_name;
    else if (v.types.includes('postal_code')) address.postalCode = v.long_name;
    else if (v.types.includes('street_number')) address.streetNumber = v.long_name;
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  address.placeId = value.place_id;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  address.latitude = value?.geometry?.location.lat();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  address.longitude = value?.geometry?.location.lng();

  return address;
};

const organizeEditAddressManual = (cmsData: IEditAddressCmsConfig): ICmsConfigAddressManual => ({
  addressInputCard: {
    title: '',
    description: '',
    labels: {
      title: cmsData?.editaddressconfig?.title || 'Cambia indirizzo',
      street: cmsData?.editaddressconfig?.labels?.street || 'Indirizzo',
      streetNumber: cmsData?.editaddressconfig?.labels?.streetNumber || 'Numero civico',
      displayStateOrProvince: cmsData?.editaddressconfig?.labels?.stateOrProvince || 'Provincia',
      city: cmsData?.editaddressconfig?.labels?.city || 'Comune',
      postalCode: cmsData?.editaddressconfig?.labels?.postalCode || 'CAP',
    },
    buttonLabel: cmsData?.editaddressconfig?.submitButton_manual || 'AGGIORNA MANUALMENTE',
  },
  addressErrors: {
    city: {
      min: 'Campo non valido',
      matchRegex: 'Campo non valido',
      required: 'Campo obbligatorio',
    },
    postalCode: {
      min: 'Campo non valido',
      matchRegex: 'Campo non valido',
      required: 'Campo obbligatorio',
    },
    stateOrProvince: {
      min: 'Campo non valido',
      matchRegex: 'Campo non valido',
      required: 'Campo obbligatorio',
    },
    street: {
      min: 'Campo non valido',
      matchRegex: 'Campo non valido',
      required: 'Campo obbligatorio',
    },
    streetNumber: {
      min: 'Campo non valido',
      matchRegex: 'Campo non valido',
      required: 'Campo obbligatorio',
    },
  },
  genericErrorAddress:
    cmsData?.editaddressconfig?.apiError ||
    "Non è stato possibile trovare l'indirizzo. La preghiamo di ricontrollare i dati inseriti.",
});

const getAllProductsByAlternatives = (product?: IProduct | null, allProducts?: IProduct[]) => {
  if (!product) return [];
  const alternativeProductOfferList = product?.alternativeProductOfferList || [];
  return allProducts?.filter((p) =>
    alternativeProductOfferList?.includes(p.cmsId?.toString() || '')
  );
};

export {
  LOCKIN_ERROR_CODES,
  getCoverageToolFlow,
  organizeFlowsAppend,
  getAddress,
  coverageToolLockInMessage,
  getTrackError,
  checkCoverageToolType,
  retrieveAddress,
  checkAddressFromValue,
  getCoverageInputValidation,
  composeAddress,
  organizeEditAddressManual,
  getAllProductsByAlternatives
};
